import { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { resizeImage, resizeLogo } from '../events/util';
import { venueUrl } from '../tenant';
import { TEMAS, TIPOGRAFIAS, cargarFuente } from '../tema';

const INK = '#9A9A9A';

const SECTIONS = [
  { key: 'noches', label: 'Las noches', desc: 'Próximos eventos publicados', icon: '🎟️', fixed: true },
  { key: 'galeria', label: 'Galería', desc: 'Álbumes de fotos de las noches', icon: '📸' },
  { key: 'sessions', label: 'Canal de YouTube', desc: 'Videos de YouTube (sets, contenido)', icon: '🎧' },
  { key: 'privados', label: 'Privados', desc: 'Tu texto y tu foto para cumpleaños y corporativos', icon: '🥂' },
  { key: 'puntos', label: 'Sistema de puntos', desc: 'Catálogo de premios canjeables', icon: '⭐' },
  { key: 'ruleta', label: 'Ruleta de descuentos', desc: 'Popup de cupones para visitantes', icon: '🎰' },
];

const TABS = [['identidad', 'Identidad'], ['diseno', 'Diseño'], ['secciones', 'Secciones']];

// Los cuatro colores del tema, con lo que hace cada uno. El dueño necesita saber qué está
// tocando: "secundario" no significa nada si no se aclara que es el color de los textos.
const COLORES = [
  { campo: 'primary_color', label: 'Principal', desc: 'Botones, acentos y detalles.' },
  { campo: 'color_secundario', label: 'Secundario', desc: 'Los textos sobre el fondo.' },
  { campo: 'color_fondo', label: 'Fondo', desc: 'El fondo de toda la web.' },
  { campo: 'color_superficie', label: 'Superficies', desc: 'Tarjetas, bordes y cajas.' },
];

export default function MiWeb() {
  const api = useApi();
  const [org, setOrg] = useState(null);
  const [tab, setTab] = useState('identidad');
  const [sections, setSections] = useState({});
  const [brand, setBrand] = useState({ hero_eyebrow: '', hero_name: '', tagline: '', footer_text: '', hero_url: null, logo_url: null, whatsapp: '', notify_email: '', privados_titulo: '', privados_texto: '', privados_img: null });
  const [saved, setSaved] = useState('');
  // Qué ruleta sale en la portada: '' = automática (la del próximo evento con ruleta activa).
  const [portada, setPortada] = useState('');
  const [activas, setActivas] = useState([]);

  useEffect(() => {
    api.get('/api/org').then(o => {
      setOrg(o);
      const s = o.active_sections || {};
      const init = {}; SECTIONS.forEach(x => { init[x.key] = x.fixed ? true : (s[x.key] !== false ? !!s[x.key] : false); });
      SECTIONS.forEach(x => { if (s[x.key] === undefined) init[x.key] = true; });
      init.noches = true;
      setSections(init);
      setBrand({
        hero_eyebrow: o.hero_eyebrow || '', hero_name: o.hero_name || '', tagline: o.tagline || '',
        footer_text: o.footer_text || '', sessions_title: o.sessions_title || '',
        hero_url: o.hero_url || null, logo_url: o.logo_url || null,
        whatsapp: o.whatsapp || '', notify_email: o.notify_email || '',
        privados_titulo: o.privados_titulo || '', privados_texto: o.privados_texto || '', privados_img: o.privados_img || null,
        primary_color: o.primary_color || '', color_secundario: o.color_secundario || '',
        color_fondo: o.color_fondo || '', color_superficie: o.color_superficie || '',
        tipografia: o.tipografia || 'default', tema: o.tema || '',
      });
    }).catch(() => {});
  }, []); // eslint-disable-line

  // La tipografía elegida se carga de verdad, para que la muestra "Aa" no mienta.
  useEffect(() => cargarFuente(brand.tipografia), [brand.tipografia]);

  // Las ruletas que se pueden poner en la portada: eventos que todavía no pasaron y tienen la
  // ruleta prendida. La ruleta se arma en cada evento (Promociones → Ruleta); acá solo se
  // elige cuál se muestra al entrar a la web.
  useEffect(() => {
    api.get('/api/org').then(o => setPortada(o.ruleta_portada_event_id || '')).catch(() => {});
    api.get('/api/roulette/activas').then(setActivas).catch(() => {});
  }, []); // eslint-disable-line
  async function cambiarPortada(v) {
    setPortada(v);
    try { await api.patch('/api/org', { ruleta_portada_event_id: v || null }); }
    catch (e) { alert(e.message); }
  }

  async function toggle(key) {
    if (key === 'noches') return;
    const next = { ...sections, [key]: !sections[key] };
    setSections(next);
    try { await api.put('/api/org/sections', { active_sections: next }); } catch (e) { alert(e.message); }
  }
  const onHero = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const url = await resizeImage(file, 1600, 0.82); setBrand(b => ({ ...b, hero_url: url })); } catch (err) { alert(err.message); }
  };
  // Foto de la sección "Privados": se guarda como el hero (redimensionada), porque se
  // muestra grande al costado del texto.
  const onPrivados = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const url = await resizeImage(file, 1400, 0.82); setBrand(b => ({ ...b, privados_img: url })); } catch (err) { alert(err.message); }
  };
  const onLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const url = await resizeLogo(file, 400); setBrand(b => ({ ...b, logo_url: url })); } catch (err) { alert(err.message); }
  };
  // Tocar un color a mano deja el tema en "a medida": ya no es ninguno de los predefinidos.
  const setColor = (campo, valor) => setBrand(b => ({ ...b, [campo]: valor, tema: 'custom' }));
  const aplicarTema = (t) => setBrand(b => ({
    ...b, tema: t.id, primary_color: t.principal, color_secundario: t.secundario,
    color_fondo: t.fondo, color_superficie: t.superficie, tipografia: t.tipografia,
  }));
  const temaPorDefecto = () => setBrand(b => ({
    ...b, tema: '', primary_color: '', color_secundario: '', color_fondo: '', color_superficie: '', tipografia: 'default',
  }));

  // Un color vacío viaja como null: es lo que devuelve la web al tema original de TukiPro.
  async function guardar() {
    const payload = { ...brand };
    // Vacío = usar el texto por defecto de la web, no guardar un título en blanco.
    ['privados_titulo', 'privados_texto'].forEach(k => { payload[k] = (payload[k] || '').trim() || null; });
    COLORES.forEach(c => { if (!payload[c.campo]) payload[c.campo] = null; });
    if (!payload.tema) payload.tema = null;
    try { await api.patch('/api/org', payload); setSaved('Guardado ✓'); setTimeout(() => setSaved(''), 2000); }
    catch (e) { alert(e.message); }
  }

  if (!org) return <div style={{ color: INK }}>Cargando…</div>;

  const tipo = TIPOGRAFIAS[brand.tipografia] || TIPOGRAFIAS.default;

  return (
    <div className="lb-fade">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div className="lb-display" style={{ fontSize: 24 }}>Mi web</div>
          <p style={{ color: INK, fontSize: 14, marginTop: 4 }}>Personalizá la web pública de tu venue: identidad, colores y qué secciones se muestran.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {saved && <span style={{ color: '#2ecc71', fontSize: 12 }}>{saved}</span>}
          {org.slug && <a className="lb-btn lb-btn--ghost lb-btn--sm" href={venueUrl(org.slug)} target="_blank" rel="noreferrer">Ver mi web ↗</a>}
          <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={guardar}>Guardar cambios</button>
        </div>
      </div>

      <div className="lb-subtabs" style={{ margin: '18px 0 22px' }}>
        {TABS.map(([k, l]) => (
          <button key={k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === 'identidad' && (
        <div className="lb-card" style={{ padding: '22px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="Logo / isotipo del venue">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 52, height: 52, borderRadius: 4, background: brand.logo_url ? 'center/contain no-repeat #0E0E0E url(' + brand.logo_url + ')' : 'var(--ink-200)', border: '1px solid var(--border)', flex: 'none' }} />
              <label className="lb-btn lb-btn--ghost lb-btn--sm" style={{ cursor: 'pointer' }}>{brand.logo_url ? 'Cambiar logo' : 'Subir logo'}<input type="file" accept="image/*" onChange={onLogo} style={{ display: 'none' }} /></label>
            </div>
          </Field>
          <Field label="Imagen del hero (fondo)">
            <label className="lb-btn lb-btn--ghost lb-btn--sm" style={{ cursor: 'pointer', alignSelf: 'start' }}>{brand.hero_url ? 'Imagen ✓ · cambiar' : 'Subir imagen'}<input type="file" accept="image/*" onChange={onHero} style={{ display: 'none' }} /></label>
          </Field>
          <Field label="Línea superior del hero"><input className="lb-input" value={brand.hero_eyebrow} onChange={e => setBrand({ ...brand, hero_eyebrow: e.target.value })} placeholder="Ej: VIVÍ LA NOCHE" /></Field>
          <Field label="Nombre del hero (texto grande)"><input className="lb-input" value={brand.hero_name} onChange={e => setBrand({ ...brand, hero_name: e.target.value })} placeholder="Ej: TU MARCA." /></Field>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Tagline / eslogan (usá *asteriscos* para una palabra en itálica)">
              <input className="lb-input" value={brand.tagline} onChange={e => setBrand({ ...brand, tagline: e.target.value })} placeholder="Ej: LA *noche* es TUYA." />
            </Field>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label="Texto del footer (pie de las páginas públicas)">
              <input className="lb-input" value={brand.footer_text} onChange={e => setBrand({ ...brand, footer_text: e.target.value })} placeholder="Acceso reservado · Dress code estricto · +18" />
            </Field>
          </div>
          <Field label="Título de la sección de videos (home)"><input className="lb-input" value={brand.sessions_title} onChange={e => setBrand({ ...brand, sessions_title: e.target.value })} placeholder="Ej: SESIONES." /></Field>
          <Field label="WhatsApp (privados)"><input className="lb-input" value={brand.whatsapp} onChange={e => setBrand({ ...brand, whatsapp: e.target.value })} placeholder="5493401XXXXXX" /></Field>
          {/* El texto y la foto de Privados se editan en la pestaña Secciones, junto a su
              interruptor: es donde se los busca. Acá solo queda el WhatsApp, que es el que
              usa su botón de consulta. */}
          <Field label="Email de notificaciones (stock)"><input className="lb-input" value={brand.notify_email} onChange={e => setBrand({ ...brand, notify_email: e.target.value })} placeholder="dueño@tuboliche.com" /></Field>
        </div>
      )}

      {tab === 'diseno' && (
        <>
          <div className="lb-card" style={{ padding: '22px 24px', marginBottom: 18 }}>
            <div className="lb-label" style={{ marginBottom: 12 }}>Temas predefinidos</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
              {TEMAS.map(t => (
                <button key={t.id} onClick={() => aplicarTema(t)} style={{ cursor: 'pointer', textAlign: 'left', padding: 10, borderRadius: 8, background: t.fondo, border: '2px solid ' + (brand.tema === t.id ? t.principal : 'var(--border)'), width: 110 }}>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                    {[t.principal, t.secundario, t.superficie].map((c, i) => (
                      <span key={i} style={{ width: 18, height: 18, borderRadius: 4, background: c, border: '1px solid rgba(128,128,128,0.4)' }} />
                    ))}
                  </div>
                  <span style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: t.secundario }}>{t.nombre}</span>
                </button>
              ))}
              <button onClick={temaPorDefecto} className="lb-btn lb-btn--ghost lb-btn--sm">Volver al tema por defecto</button>
            </div>
            <p style={{ color: INK, fontSize: 12, marginTop: 14, lineHeight: 1.5 }}>
              Un tema solo carga los colores y la tipografía de abajo: después cambiás lo que quieras. Si dejás
              todo vacío, tu web usa el tema original de TukiPro. Los cambios se ven al guardar.
            </p>
          </div>

          <div className="lb-card" style={{ padding: '22px 24px', marginBottom: 18 }}>
            <div className="lb-label" style={{ marginBottom: 12 }}>Paleta de colores</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))', gap: 16 }}>
              {COLORES.map(c => (
                <div key={c.campo}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="color" value={brand[c.campo] || '#000000'} onChange={e => setColor(c.campo, e.target.value.toUpperCase())}
                      style={{ width: 44, height: 44, padding: 0, border: '1px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer', flex: 'none' }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>{c.label}</div>
                      <div style={{ fontSize: 11.5, color: INK }}>{c.desc}</div>
                    </div>
                  </div>
                  <input className="lb-input" style={{ marginTop: 8, fontSize: 13 }} value={brand[c.campo] || ''}
                    onChange={e => setColor(c.campo, e.target.value.toUpperCase())} placeholder="#RRGGBB (vacío = por defecto)" />
                </div>
              ))}
            </div>
          </div>

          <div className="lb-card" style={{ padding: '22px 24px' }}>
            <div className="lb-label" style={{ marginBottom: 12 }}>Tipografía</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
              <select className="lb-select" style={{ maxWidth: 260 }} value={brand.tipografia}
                onChange={e => setBrand(b => ({ ...b, tipografia: e.target.value, tema: 'custom' }))}>
                {Object.entries(TIPOGRAFIAS).map(([k, t]) => <option key={k} value={k}>{t.nombre}</option>)}
              </select>
              <div style={{ lineHeight: 1 }}>
                <div style={{ fontFamily: tipo.display, fontSize: 34, color: '#fff' }}>Aa</div>
                <div style={{ fontFamily: tipo.body, fontSize: 13, color: INK, marginTop: 6 }}>Así se ve tu web</div>
              </div>
            </div>
          </div>
        </>
      )}

      {tab === 'secciones' && (
        <><div className="lb-card" style={{ padding: '6px 22px' }}>
          {SECTIONS.map(s => (
            <div key={s.key} className="lb-row" style={{ gridTemplateColumns: '40px 1fr auto auto', alignItems: 'center' }}>
              <span style={{ fontSize: 22 }}>{s.icon}</span>
              <div>
                <div style={{ color: '#fff', fontWeight: 600 }}>{s.label} {s.fixed && <span className="lb-status lb-status--gen" style={{ marginLeft: 8 }}>Fijo</span>}</div>
                <div style={{ fontSize: 12, color: INK }}>{s.desc}</div>
              </div>
              <span className={'lb-status ' + (sections[s.key] ? 'lb-status--paid' : 'lb-status--gen')}>{sections[s.key] ? 'Activo' : 'Inactivo'}</span>
              <button onClick={() => toggle(s.key)} disabled={s.fixed} className={'lb-toggle__track ' + (sections[s.key] ? 'lb-toggle__track--on' : 'lb-toggle__track--off')} style={{ border: 0, opacity: s.fixed ? 0.5 : 1, cursor: s.fixed ? 'not-allowed' : 'pointer' }}>
                <span className={'lb-toggle__knob ' + (sections[s.key] ? 'lb-toggle__knob--on' : 'lb-toggle__knob--off')} />
              </button>
            </div>
          ))}
          <p style={{ color: INK, fontSize: 12, padding: '10px 0 16px' }}>Las secciones se guardan solas al tocar el interruptor. "Las noches" no se puede apagar: es el corazón de la web.</p>
        </div>
        {/* La sección Privados se edita acá abajo, donde está su interruptor: es el primer
            lugar donde se la busca. El botón Guardar de arriba es el que la guarda. */}
        {sections.privados && (
          <div className="lb-card" style={{ padding: '18px 22px', marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
              <div className="lb-h" style={{ fontSize: 15 }}>🥂 Qué dice tu sección de Privados</div>
              <button className="lb-btn lb-btn--primary lb-btn--sm" onClick={guardar}>Guardar cambios</button>
            </div>
            <p style={{ color: INK, fontSize: 12, margin: '0 0 16px', lineHeight: 1.5 }}>
              Es el bloque donde ofrecés cumpleaños, egresados o eventos corporativos: un texto con una foto al costado.
              Si dejás el título o el texto vacíos, la web usa los de por defecto. El botón de consulta usa tu WhatsApp, que se carga en Identidad.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="tk-2col">
              <Field label="Título de la sección">
                <input className="lb-input" value={brand.privados_titulo} onChange={e => setBrand({ ...brand, privados_titulo: e.target.value })} placeholder="Privados" />
              </Field>
              <Field label="Foto">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 84, height: 52, borderRadius: 4, flex: 'none', background: brand.privados_img ? 'center/cover no-repeat #0E0E0E url(' + brand.privados_img + ')' : 'var(--ink-200)', border: '1px solid var(--border)' }} />
                  <label className="lb-btn lb-btn--ghost lb-btn--sm" style={{ cursor: 'pointer' }}>{brand.privados_img ? 'Cambiar foto' : 'Subir foto'}<input type="file" accept="image/*" onChange={onPrivados} style={{ display: 'none' }} /></label>
                  {brand.privados_img && <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setBrand({ ...brand, privados_img: null })}>Quitar</button>}
                </div>
              </Field>
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="Texto">
                  <textarea className="lb-input" style={{ minHeight: 120, fontSize: 14.5, lineHeight: 1.6 }} value={brand.privados_texto}
                    onChange={e => setBrand({ ...brand, privados_texto: e.target.value })}
                    placeholder="Ej: Festejá tu cumpleaños de 15, tu egresados o tu evento corporativo con nuestra producción. Escribinos y armamos la noche a tu medida." />
                </Field>
              </div>
            </div>
          </div>
        )}

        {sections.ruleta && (
          <div className="lb-card" style={{ padding: '18px 22px', marginTop: 16 }}>
            <div className="lb-label" style={{ marginBottom: 8 }}>Qué ruleta sale en la portada</div>
            <select className="lb-select" style={{ maxWidth: 420 }} value={portada} onChange={e => cambiarPortada(e.target.value)}>
              <option value="">Automática: la del próximo evento con ruleta activa</option>
              {activas.map(e => (
                <option key={e.id} value={e.id}>{e.name}{e.date ? ' · ' + String(e.date).slice(0, 10).split('-').reverse().join('/') : ''}</option>
              ))}
              {portada && !activas.some(e => e.id === portada) && (
                <option value={portada}>Evento elegido: ya no está disponible (se usa la automática)</option>
              )}
            </select>
            <p style={{ color: INK, fontSize: 12, marginTop: 10, lineHeight: 1.5 }}>
              Solo aparecen los eventos que todavía no pasaron y tienen la ruleta prendida. La ruleta se arma en cada evento, en Promociones → Ruleta.
              {activas.length === 0 && ' Ahora no hay ninguna prendida, así que la portada no muestra ruleta.'}
            </p>
          </div>
        )}
        </>
      )}
    </div>
  );
}

function Field({ label, children }) { return <div className="lb-field"><div className="lb-label">{label}</div>{children}</div>; }
